/**
 * Multi-Service API Configuration
 * Defines endpoints for each dedicated service
 */

export interface ServiceEndpoints {
	auth: string;
	tenant: string;
	registry: string;
	analytics: string;
	storage: string;
	monitoring: string;
}

export interface ServicesConfig {
	services: ServiceEndpoints;
	timeout: number;
	retries: number;
	environment: "development" | "staging" | "production";
	features: {
		enableDebugLogs: boolean;
		enableRetries: boolean;
		enableCaching: boolean;
	};
}

/**
 * Environment-specific service configurations
 */
const SERVICES_CONFIGURATIONS: Record<string, ServicesConfig> = {
	development: {
		services: {
			auth: "https://auth-service.cto-41f.workers.dev",
			tenant: "https://tenant-service.cto-41f.workers.dev",
			registry: "https://registry-service.cto-41f.workers.dev",
			analytics: "https://analytics-service.cto-41f.workers.dev",
			storage: "https://storage-service.cto-41f.workers.dev",
			monitoring: "https://monitoring-service.cto-41f.workers.dev",
		},
		timeout: 30000,
		retries: 3,
		environment: "development",
		features: {
			enableDebugLogs: true,
			enableRetries: true,
			enableCaching: false,
		},
	},

	staging: {
		services: {
			auth: "https://auth-staging.artify.monster",
			tenant: "https://tenant-staging.artify.monster",
			registry: "https://registry-staging.artify.monster",
			analytics: "https://analytics-staging.artify.monster",
			storage: "https://storage-staging.artify.monster",
			monitoring: "https://monitoring-staging.artify.monster",
		},
		timeout: 20000,
		retries: 2,
		environment: "staging",
		features: {
			enableDebugLogs: true,
			enableRetries: true,
			enableCaching: true,
		},
	},

	production: {
		services: {
			auth: "https://auth.artify.monster",
			tenant: "https://tenant.artify.monster",
			registry: "https://api.artify.monster", // Main API gateway routes to registry
			analytics: "https://analytics.artify.monster",
			storage: "https://storage.artify.monster",
			monitoring: "https://monitoring.artify.monster",
		},
		timeout: 15000,
		retries: 2,
		environment: "production",
		features: {
			enableDebugLogs: false,
			enableRetries: true,
			enableCaching: true,
		},
	},
};

/**
 * Get current environment from environment variables
 */
function getCurrentEnvironment(): keyof typeof SERVICES_CONFIGURATIONS {
	const env =
		import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE || "development";

	if (env in SERVICES_CONFIGURATIONS) {
		return env as keyof typeof SERVICES_CONFIGURATIONS;
	}

	return "development";
}

/**
 * Get services configuration for current environment
 */
export function getServicesConfig(): ServicesConfig {
	const environment = getCurrentEnvironment();
	return SERVICES_CONFIGURATIONS[environment];
}

/**
 * Get specific service base URL
 */
export function getServiceUrl(service: keyof ServiceEndpoints): string {
	const config = getServicesConfig();
	return config.services[service];
}

/**
 * Build full URL for a service endpoint
 */
export function buildServiceUrl(
	service: keyof ServiceEndpoints,
	path: string = "",
): string {
	const baseUrl = getServiceUrl(service);
	const cleanPath = path.startsWith("/") ? path : `/${path}`;
	return `${baseUrl}${cleanPath}`;
}

/**
 * Build tenant-scoped URL for tenant service
 */
export function buildTenantUrl(tenantSlug: string, path: string = ""): string {
	const tenantServiceUrl = getServiceUrl("tenant");
	const cleanPath = path.startsWith("/") ? path : `/${path}`;
	return `${tenantServiceUrl}/t/${tenantSlug}${cleanPath}`;
}

/**
 * Build registry URL with tenant context
 */
export function buildRegistryUrl(
	tenantSlug: string,
	path: string = "",
): string {
	const registryServiceUrl = getServiceUrl("registry");
	const cleanPath = path.startsWith("/") ? path : `/${path}`;
	return `${registryServiceUrl}/api/t/${tenantSlug}${cleanPath}`;
}

/**
 * Check if debug logging is enabled
 */
export function isServicesDebugEnabled(): boolean {
	const config = getServicesConfig();
	return config.features.enableDebugLogs;
}
